import { FeedForDeployment, FeedNotification, QuestionFeed, QuestionFeedCreate, QuestionFeedUpdate } from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";

import { QuestionFeedsAPI } from "api";

const queryKeys = {
  all: () => ["question-feeds"] as const,
  detail: (id: number) => [...queryKeys.all(), id] as const,
  notifications: (id: number) => [...queryKeys.detail(id), "notifications"] as const,
  deploymentList: () => [...queryKeys.all(), "deployment"] as const,
};

/**
 * Get all feed notifications.
 */
export const useQuestionFeedNotifications = <T extends FeedNotification>(
  id: number,
  options: UseQueryOptions<FeedNotification[], unknown, T[]>,
) =>
  useQuery<FeedNotification[], unknown, T[]>(
    queryKeys.notifications(id),
    () => QuestionFeedsAPI.listNotifications(id),
    options,
  );

/**
 * Mutation to create a new question feed.
 */
export const useQuestionFeedCreate = (options: UseMutationOptions<QuestionFeed, unknown, QuestionFeedCreate>) =>
  useMutation((req) => QuestionFeedsAPI.create(req), options);

/**
 * Mutation to update a question feed.
 */
export const useQuestionFeedUpdate = (options: UseMutationOptions<QuestionFeed, unknown, QuestionFeedUpdate>) =>
  useMutation((req) => QuestionFeedsAPI.update(req), options);

/**
 * Mutation to delete a question feed.
 */
export const useQuestionFeedDelete = (options: UseMutationOptions<void, unknown, number>) =>
  useMutation((req) => QuestionFeedsAPI.delete(req), options);

/**
 * Update all feed notifications.
 */
export const useUpdateQuestionFeedNotifications = (
  id: number,
  options: UseMutationOptions<FeedNotification[], unknown, FeedNotification[]>,
) => useMutation((notifications) => QuestionFeedsAPI.updateNotifications(id, notifications), options);

/**
 * Fetch question feeds for deployment.
 */
export const useQuestionFeedsForDeployment = <T = FeedForDeployment[]>(
  options: UseQueryOptions<FeedForDeployment[], unknown, T> = {},
) =>
  useQuery<FeedForDeployment[], unknown, T>(
    queryKeys.deploymentList(),
    () => QuestionFeedsAPI.listForDeployment(),
    options,
  );
